import React, { useContext, useEffect, useRef, useState } from "react";

import makeStyles from "@mui/styles/makeStyles";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import TabPanel from "../TabPanel";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { Button } from "@mui/material";

const useStyles = makeStyles(theme => ({
	ticketsWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	tabsHeader: {
		flex: "none",
		backgroundColor: theme.palette.background.paper,
		borderBottom: `1px solid ${theme.palette.divider}`,
	},

	settingsIcon: {
		alignSelf: "center",
		marginLeft: "auto",
		padding: 8,
	},

	tab: {
		minWidth: 120,
		width: 120,
	},

	ticketOptionsBox: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		background: theme.palette.background.default,
		borderBottom: `1px solid ${theme.palette.divider}`,
		padding: theme.spacing(1),
		gap: theme.spacing(1),
	},

	serachInputWrapper: {
		flex: 1,
		background: theme.palette.background.paper,
		display: "flex",
		borderRadius: 40,
		padding: 4,
		marginRight: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
	},

	searchIcon: {
		color: theme.palette.text.secondary,
		marginLeft: 6,
		marginRight: 6,
		alignSelf: "center",
	},

	searchInput: {
		flex: 1,
		border: "none",
		borderRadius: 30,
		color: theme.palette.text.primary,
	},

	showAllLabel: {
		color: theme.palette.text.primary,
		margin: 0,
		"& .MuiFormControlLabel-label": {
			color: theme.palette.text.primary,
			fontSize: "0.875rem",
		},
	},
}));

const TicketsManager = () => {
	const classes = useStyles();

	const [searchParam, setSearchParam] = useState("");
	const [tab, setTab] = useState("open");
	const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
	const [showAllTickets, setShowAllTickets] = useState(false);
	const searchInputRef = useRef();
	const { user } = useContext(AuthContext);

	const userQueueIds = (user?.queues ?? []).map(q => q.id);
	const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);

	useEffect(() => {
		if (tab === "search") {
			searchInputRef.current.focus();
		}
	}, [tab]);

	let searchTimeout;

	const handleSearch = e => {
		const searchedTerm = e.target.value.toLowerCase();
		clearTimeout(searchTimeout);
		if (searchedTerm.length <= 3) return;
		if (searchedTerm === "") {
			setSearchParam(searchedTerm);
			setTab("open");
			return;
		}

		searchTimeout = setTimeout(() => {
			setSearchParam(searchedTerm);
		}, 500);
	};

	const handleChangeTab = (e, newValue) => {
		setTab(newValue);
	};

	return (
		<Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
			<NewTicketModal
				modalOpen={newTicketModalOpen}
				onClose={e => setNewTicketModalOpen(false)}
			/>
			<Paper elevation={0} square className={classes.tabsHeader}>
				<Tabs
					value={tab}
					onChange={handleChangeTab}
					variant="fullWidth"
					indicatorColor="primary"
					textColor="primary"
					aria-label="icon label tabs example"
				>
					<Tab
						value={"open"}
						icon={<MoveToInboxIcon />}
						label={i18n.t("tickets.tabs.open.title")}
						classes={{ root: classes.tab }}
					/>
					<Tab
						value={"closed"}
						icon={<CheckBoxIcon />}
						label={i18n.t("tickets.tabs.closed.title")}
						classes={{ root: classes.tab }}
					/>
					<Tab
						value={"search"}
						icon={<SearchIcon />}
						label={i18n.t("tickets.tabs.search.title")}
						classes={{ root: classes.tab }}
					/>
				</Tabs>
			</Paper>
			<Paper square elevation={0} className={classes.ticketOptionsBox}>
				{tab === "search" ? (
					<div className={classes.serachInputWrapper}>
						<SearchIcon className={classes.searchIcon} />
						<InputBase
							className={classes.searchInput}
                                                        inputRef={searchInputRef}
                                                        placeholder={i18n.t("tickets.search.placeholder")}
                                                        type="search"
                                                        onChange={handleSearch}
                                                />
                                        </div>
                                ) : (
                                        <>
                                                <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => setNewTicketModalOpen(true)}
                                                >
                                                        {i18n.t("ticketsManager.buttons.newTicket")}
                                                </Button>
                                                <Can
                                                        role={user.profile}
                                                        perform="tickets-manager:showall"
                                                        yes={() => (
                                                                <FormControlLabel
                                                                        className={classes.showAllLabel}
                                                                        label={i18n.t("tickets.buttons.showAll")}
                                                                        labelPlacement="start"
                                                                        control={
                                                                                <Switch
                                                                                        size="small"
                                                                                        checked={showAllTickets}
                                                                                        onChange={() =>
                                                                                                setShowAllTickets(prevState => !prevState)
                                                                                        }
                                                                                        name="showAllTickets"
                                                                                        color="primary"
                                                                                />
                                                                        }
                                                                />
                                                        )}
                                                />
                                        </>
                                )}
                                <TicketsQueueSelect
                                        style={{ marginLeft: 6 }}
                                        selectedQueueIds={selectedQueueIds}
                                        userQueues={user?.queues}
                                        onChange={values => setSelectedQueueIds(values)}
                                />
                        </Paper>
                        <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
                                <TicketsList
                                        status="open"
                                        showAll={showAllTickets}
                                        selectedQueueIds={selectedQueueIds}
                                />
                                <TicketsList status="pending" showAll={showAllTickets} selectedQueueIds={selectedQueueIds} />
                        </TabPanel>
                        <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
                                <TicketsList
                                        status="closed"
                                        //showAll={true}
                                        showAll={showAllTickets}
                                        selectedQueueIds={selectedQueueIds}
                                />
                        </TabPanel>
                        <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
                                <TicketsList
                                        searchParam={searchParam}
                                        //showAll={true}
                                        showAll={showAllTickets}
                                        selectedQueueIds={selectedQueueIds}
                                />
                        </TabPanel>
                </Paper>
        );
};

export default TicketsManager;

